import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from '../components/Icon';
import { useDocumentHead } from '../lib/seo';

export default function FaqPage() {
  const navigate = useNavigate();

  useDocumentHead({
    title: 'How ParkProof works · FAQ',
    description:
      'How ParkProof finds parking heights for tall cars, SUVs, vans, and roof boxes — and answers to common questions.',
    canonicalPath: '/faq',
  });

  return (
    <div className="flex flex-col">
      <div className="px-s4 py-s2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      <article className="p-s5 flex flex-col gap-s6">
        <header>
          <div className="caption text-ink3 font-mono mb-s2">FAQ</div>
          <h1 className="font-serif text-[32px] leading-[36px] tracking-tight text-ink1">
            Built for cars that <span className="font-serif-italic">don't fit</span>
          </h1>
          <p className="mt-s3 text-[14px] md:text-[15px] text-ink2 font-sans leading-relaxed">
            Most parking apps care about price and availability. ParkProof cares about one thing:
            whether the bar at the entrance is high enough for <em>your</em> car. We maintain
            driver-verified clearance heights for parking garages across Europe, with a clear
            verdict for every car — including SUVs, vans, pickups, and any car with a roof box or
            roof rack on top.
          </p>
        </header>

        <section aria-labelledby="how-it-works-heading" className="flex flex-col gap-s3">
          <h2
            id="how-it-works-heading"
            className="font-serif font-medium text-[22px] tracking-tight text-ink1"
          >
            How it works
          </h2>
          <p className="text-[14px] text-ink2 font-sans leading-relaxed">
            ParkProof used to make you pick a city first. Now you just type where you're going —
            a restaurant, an address, a landmark, anywhere in Europe — and we show every parking
            garage within reach of that point, not just a fixed set of launch cities.
          </p>
          <p className="text-[14px] text-ink2 font-sans leading-relaxed">
            The moment you search a new area, ParkProof pulls fresh data from OpenStreetMap for
            that exact spot — a point plus a radius that scales with what you searched (a
            restaurant gets a tight 1.5&nbsp;km radius, a whole city gets 8&nbsp;km) — normalizes
            every parking's posted height limit, and drops it on the map with a fit verdict for
            the car you're driving. No area is off-limits; if nobody's searched it yet, your
            search is what triggers the ingest.
          </p>
          <p className="text-[14px] text-ink2 font-sans leading-relaxed">
            Heights start life as "likely" — sourced from OpenStreetMap or an operator's posted
            sign, never independently confirmed. Any driver can upgrade a garage to{' '}
            <em>verified</em> by measuring it in person: type in the number you read at the
            entrance, or take a photo of the sign and let our OCR pipeline read it for you. Two
            independent photo measurements that agree lock in the verified tier.
          </p>
          <p className="text-[14px] text-ink2 font-sans leading-relaxed">
            Toggle a roof rack (+10&nbsp;cm) or roof box (+40&nbsp;cm) on top of your car's stock
            height, and every verdict on the map re-evaluates instantly against that total.
          </p>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-s4">
          <div>
            <h3 className="font-sans font-semibold text-[14px] text-ink1 mb-2">
              Who uses ParkProof
            </h3>
            <ul className="text-[13px] text-ink2 font-sans leading-relaxed list-disc pl-5 space-y-1">
              <li>Drivers of SUVs and 4×4s (Tesla Model Y, Volvo XC90, Audi Q7, Range Rover…)</li>
              <li>Drivers of vans and minivans (VW Bus, Mercedes Vito, Ford Transit Custom)</li>
              <li>Pickup-truck drivers</li>
              <li>Anyone with a roof box on a ski trip or summer holiday</li>
              <li>Roof-rack drivers carrying bikes, kayaks, or surfboards</li>
              <li>Delivery drivers and tradespeople in tall work vans</li>
              <li>Rental-car drivers in an unfamiliar city</li>
            </ul>
          </div>
          <div>
            <h3 className="font-sans font-semibold text-[14px] text-ink1 mb-2">
              In three steps
            </h3>
            <ol className="text-[13px] text-ink2 font-sans leading-relaxed list-decimal pl-5 space-y-1">
              <li>Type where you're going — a destination, not a city.</li>
              <li>See every parking around it, colored by confidence and your car's fit.</li>
              <li>
                Pick your car (or use the default) — get a clear <em>Fits</em>, <em>Tight</em>,{' '}
                <em>Doesn't fit</em>, or <em>Unknown</em> verdict.
              </li>
            </ol>
          </div>
        </section>

        <section>
          <h3 className="font-sans font-semibold text-[14px] text-ink1 mb-2">
            Countries we cover
          </h3>
          <p className="text-[13px] text-ink2 font-sans leading-relaxed">
            ParkProof covers all of Europe — the EU, EFTA, the UK, the Balkans, and the
            microstates. There's no fixed list of launch cities anymore: search any destination,
            and if we don't have it yet, your search is what brings it onto the map.
          </p>
        </section>

        <section>
          <h2
            id="faq-heading"
            className="font-serif font-medium text-[20px] tracking-tight text-ink1 mb-s3"
          >
            Frequently asked
          </h2>
          <dl className="flex flex-col gap-s4">
            <div>
              <dt className="font-sans font-semibold text-[13px] text-ink1">
                Will my SUV fit in a parking garage?
              </dt>
              <dd className="mt-1 text-[13px] text-ink2 font-sans leading-relaxed">
                Tell ParkProof your car's height (or pick the model — height auto-fills), and
                every nearby garage shows a green / amber / red verdict based on its clearance. A
                typical SUV is 1.65–1.85&nbsp;m. Many European garages cap at 2.00&nbsp;m, and
                quite a few at 1.90&nbsp;m or even 1.80&nbsp;m — closer than people think.
              </dd>
            </div>
            <div>
              <dt className="font-sans font-semibold text-[13px] text-ink1">
                What if I have a roof box or roof rack?
              </dt>
              <dd className="mt-1 text-[13px] text-ink2 font-sans leading-relaxed">
                Toggle them from the garage card. ParkProof adds 10&nbsp;cm for a roof rack and
                40&nbsp;cm for a roof box on top of your car's stock height, then re-evaluates
                every garage against that total.
              </dd>
            </div>
            <div>
              <dt className="font-sans font-semibold text-[13px] text-ink1">
                Where do the heights come from?
              </dt>
              <dd className="mt-1 text-[13px] text-ink2 font-sans leading-relaxed">
                OpenStreetMap and operator-posted signage to start ("likely"), then driver
                submissions — a typed measurement or a photo of the entrance sign, read via
                on-device OCR — to reach "verified". Every value carries its source and trust
                tier.
              </dd>
            </div>
            <div>
              <dt className="font-sans font-semibold text-[13px] text-ink1">Is ParkProof free?</dt>
              <dd className="mt-1 text-[13px] text-ink2 font-sans leading-relaxed">
                Yes — free on iOS and on the web. No ads, no premium tier.
              </dd>
            </div>
            <div>
              <dt className="font-sans font-semibold text-[13px] text-ink1">
                Do I need an account?
              </dt>
              <dd className="mt-1 text-[13px] text-ink2 font-sans leading-relaxed">
                No — you can search and build a garage of cars anonymously from the first visit.
                Sign in with Google or email whenever you want your garage to follow you across
                devices; your existing anonymous garage carries over automatically.
              </dd>
            </div>
            <div>
              <dt className="font-sans font-semibold text-[13px] text-ink1">
                How does ParkProof cover a new area?
              </dt>
              <dd className="mt-1 text-[13px] text-ink2 font-sans leading-relaxed">
                Search it. If nobody has searched that spot recently, ParkProof runs an
                OpenStreetMap ingest for it on the spot — usually a few seconds — then invites
                local drivers to verify what it found.
              </dd>
            </div>
          </dl>
        </section>
      </article>
    </div>
  );
}
